import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ClipboardList,
  Clock,
  Calendar,
  User,
  CheckCircle,
} from "lucide-react";
import SampleBadge from "../ui/SampleBadge";
import { formatDate, snakeCaseToTitleCase, practiceAreaColors } from "./utils";

type UrgencyTab = "overdue" | "today" | "week";

interface TaskListWidgetProps {
  tasks: { overdue: any[]; today: any[]; week: any[] };
  onMarkDone: (taskId: string) => void;
}

const tabConfig: Record<
  UrgencyTab,
  { label: string; color: string; bgColor: string; borderColor: string; barColor: string }
> = {
  overdue: {
    label: "Overdue",
    color: "text-red-600",
    bgColor: "bg-red-50",
    borderColor: "border-red-600",
    barColor: "bg-red-600",
  },
  today: {
    label: "Due Today",
    color: "text-orange-600",
    bgColor: "bg-orange-50",
    borderColor: "border-orange-600",
    barColor: "bg-orange-600",
  },
  week: {
    label: "Due This Week",
    color: "text-yellow-600",
    bgColor: "bg-yellow-50",
    borderColor: "border-yellow-600",
    barColor: "bg-yellow-600",
  },
};

const TaskListWidget: React.FC<TaskListWidgetProps> = ({
  tasks,
  onMarkDone,
}) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<UrgencyTab>("overdue");

  const filteredTasks: any[] = tasks?.[activeTab] || [];
  const allTasksEmpty =
    !tasks?.overdue?.length &&
    !tasks?.today?.length &&
    !tasks?.week?.length;

  return (
    <section className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
      <div
        className="px-6 py-4"
        style={{ backgroundColor: "var(--color-primary)" }}
      >
        <div className="flex items-center gap-2.5">
          <ClipboardList className="h-5 w-5 text-white/80 shrink-0" aria-hidden="true" />
          <h2 className="text-xl font-bold text-white m-0">
            Urgent Tasks & Deadlines
          </h2>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200 bg-slate-50">
        <div className="flex" role="tablist" aria-label="Task urgency levels">
          {(Object.keys(tabConfig) as UrgencyTab[]).map((tab) => {
            const config = tabConfig[tab];
            const isActive = activeTab === tab;
            const count = (tasks?.[tab] || []).length;

            return (
              <button
                key={tab}
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-controls={`${tab}-panel`}
                onClick={() => setActiveTab(tab)}
                className={`relative flex-1 px-6 py-4 font-semibold text-sm transition-colors ${
                  isActive
                    ? config.color
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                <span className="flex items-center justify-center gap-2">
                  {config.label}
                  <span
                    className={`inline-flex items-center justify-center min-w-[24px] h-6 px-2 rounded-full text-xs font-bold ${
                      isActive
                        ? config.bgColor
                        : "bg-slate-200 text-slate-700"
                    }`}
                  >
                    {count}
                  </span>
                </span>

                {isActive && (
                  <motion.div
                    layoutId="taskWidgetActiveTab"
                    className={`absolute bottom-0 left-0 right-0 h-1 ${config.barColor}`}
                    transition={{
                      type: "spring",
                      stiffness: 500,
                      damping: 30,
                    }}
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Task List */}
      <div
        className="p-6"
        id={`${activeTab}-panel`}
        role="tabpanel"
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="space-y-4"
          >
            {allTasksEmpty ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div
                  className="h-16 w-16 rounded-full flex items-center justify-center mb-4"
                  style={{ backgroundColor: "var(--color-primary-light)" }}
                >
                  <CheckCircle
                    className="h-8 w-8"
                    style={{ color: "var(--color-accent)" }}
                  />
                </div>
                <p className="font-semibold text-slate-700 mb-1">
                  You're all caught up!
                </p>
                <p className="text-sm text-slate-400">
                  No urgent tasks or upcoming deadlines
                </p>
              </div>
            ) : filteredTasks.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-slate-500">
                <CheckCircle
                  className="h-12 w-12 mb-3 text-green-500"
                  aria-hidden="true"
                />
                <p className="font-medium">All clear!</p>
                <p className="text-sm">No tasks in this category</p>
              </div>
            ) : (
              filteredTasks.map((task: any, index: number) => {
                const practiceArea =
                  task.matterId?.practiceAreas?.[0] || "";
                const practiceAreaDisplay = practiceArea
                  ? snakeCaseToTitleCase(practiceArea)
                  : "";

                return (
                  <motion.div
                    key={task._id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{
                      delay: index * 0.05,
                      duration: 0.3,
                    }}
                    className="bg-slate-50 border border-slate-200 rounded-lg p-5 hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => {
                      if (task.matterId?._id) {
                        navigate(`/matters/${task.matterId._id}`);
                      }
                    }}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 space-y-2">
                        <div className="flex items-start gap-3">
                          <Clock
                            className={`h-5 w-5 mt-0.5 flex-shrink-0 ${tabConfig[activeTab].color}`}
                            aria-hidden="true"
                          />
                          <div className="flex-1">
                            <h3
                              className="font-semibold mb-1"
                              style={{ color: "#1F2937" }}
                            >
                              {task.matterId?.title || "Untitled Matter"}
                            </h3>
                            {task.clientId?.displayName && (
                              <div className="flex items-center gap-1.5 mb-2">
                                <User
                                  className="h-3.5 w-3.5 flex-shrink-0"
                                  style={{ color: "var(--color-accent)" }}
                                  aria-hidden="true"
                                />
                                <p
                                  className="text-sm font-medium"
                                  style={{ color: "var(--color-accent)" }}
                                >
                                  {task.clientId.displayName}
                                </p>
                              </div>
                            )}
                            <p className="text-sm font-medium text-slate-700">
                              {task.title}
                            </p>
                          </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-3 text-xs text-slate-600 ml-8">
                          <span className="flex items-center gap-1">
                            <Calendar
                              className="h-3.5 w-3.5"
                              aria-hidden="true"
                            />
                            {formatDate(task.dueAt)}
                          </span>

                          {task.assigneeUserIds?.[0]?.name && (
                            <span className="flex items-center gap-1">
                              <User
                                className="h-3.5 w-3.5"
                                aria-hidden="true"
                              />
                              {task.assigneeUserIds[0].name}
                            </span>
                          )}

                          {practiceAreaDisplay && (
                            <span
                              className={`px-2 py-1 rounded-full font-medium ${
                                practiceAreaColors[practiceArea] ||
                                practiceAreaColors[practiceAreaDisplay] ||
                                "bg-gray-100 text-gray-700"
                              }`}
                            >
                              {practiceAreaDisplay}
                            </span>
                          )}
                          {task.isSample && <SampleBadge />}
                        </div>
                      </div>

                      <button
                        type="button"
                        className="px-4 py-2 text-white text-sm font-medium rounded-lg transition-colors flex-shrink-0"
                        style={{
                          backgroundColor: "var(--color-primary)",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor =
                            "var(--color-primary-hover)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor =
                            "var(--color-primary)";
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onMarkDone(task._id);
                        }}
                        aria-label={`Mark ${task.title} as done`}
                      >
                        Mark Done
                      </button>
                    </div>
                  </motion.div>
                );
              })
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

export default TaskListWidget;
